<template>
  <section v-if="isVisible" class="expiring-section" data-cy="expiringOffersSection">
    <div
      :class="['expiring-section__header', {'expiring-section__header--collapsed': isCollapsed}]"
      @click="toggleCollapsed">
      <div class="expiring-section__title-wrap">
        <h3 class="expiring-section__title">{{ $t('offers.about-to-expire.title') }}</h3>
        <v-tooltip bottom color="#000" :max-width="400" content-class="tooltip-content">
          <template v-slot:activator="{on, attrs}">
            <i class="fa-light fa-circle-info expiring-section__info-icon" v-bind="attrs" @click.stop v-on="on"></i>
          </template>
          <span v-html="$t('offers.about-to-expire.tooltip')"></span>
        </v-tooltip>
      </div>
      <v-icon class="expiring-section__chevron" color="#9A9A9A" size="20">{{
        isCollapsed ? 'fa fa-chevron-down' : 'fa fa-chevron-up'
      }}</v-icon>
    </div>

    <template v-if="!isCollapsed">
      <div class="expiring-section__body">
        <v-data-table
          class="expiring-table elevation-0"
          hide-default-footer
          :headers="headers"
          :item-class="getRowClass"
          :items="data"
          :items-per-page="isExpanded ? tablePagination.itemsPerPage : DEFAULT_VISIBLE_COUNT"
          :loading="isLoading"
          :page="tablePagination.page"
          :server-items-length="total"
          @click:row="viewOfferSummary">
          <template v-slot:item.createDate="{item}">
            <span class="text-item">{{ formatDate(item.createDate) }}</span>
          </template>

          <template v-slot:item.client="{item}">
            <div v-if="item.type === OfferType.subagent" class="customer py-3 px-2 px-md-0">
              <p class="customer-header">{{ $t('offer-manager.offers-list.lbl-subagent') }}</p>
            </div>
            <m-copy
              v-else
              :text-to-copy="`${getFullName(item)}, ${item.client && item.client.email}, ${item.client && item.client.phone}`"
              :show-client-history="true"
              @onCopy="onCopyClick"
              @onHistoryClick="handleHistoryClick(item.client && item.client.id)">
              <div class="customer text-left py-1">
                <span class="customer-header">{{ getFullName(item) }}</span
                ><br />
                <span v-if="item.client && item.client.email" class="customer-email"
                  >{{ item.client.email }}<br
                /></span>
                <span v-if="item.client && item.client.phone" class="customer-phone">{{ item.client.phone }}</span>
              </div>
            </m-copy>
          </template>

          <template v-slot:item.vehicle.model="{item}">
            <span class="text-item">{{ getModel(item) }}</span>
          </template>

          <template v-slot:item.offerCode="{item}">
            <div @click.stop>
              <m-copy v-if="item.offerCode" :text-to-copy="item.offerCode" align="center" @onCopy="onCopyClick">
                <div class="text-item">{{ item.offerCode }}</div>
              </m-copy>
            </div>
          </template>

          <template v-slot:item.attendedBy="{item}">
            <span class="text-item">{{ item.attendedBy }}</span>
          </template>

          <template v-slot:item.status="{item}">
            <m-chip
              v-for="(chip, index) in [statusChip(item)]"
              :key="index"
              :icon="chip.icon"
              :text="chip.text"
              :tooltip="chip.tooltip"
              :trailing-icon="chip.trailingIcon"
              :variant="chip.variant" />
          </template>

          <template v-slot:item.followUp="{item}">
            <span v-if="item.followUp" :class="{'text-expired': isPastToday(item.followUp.dueDate)}">
              {{ formatDate(item.followUp.dueDate) }}
            </span>
          </template>

          <template v-slot:item.actions="{item}">
            <div @click.stop>
              <offer-table-actions :item="item" @changeStatus="onChangeStatus" />
            </div>
          </template>
        </v-data-table>

        <button
          v-if="total > DEFAULT_VISIBLE_COUNT"
          class="expiring-section__toggle"
          type="button"
          data-cy="expiringOffersToggle"
          @click="toggleExpanded">
          {{ isExpanded ? $t('offers.about-to-expire.show-less') : $t('offers.about-to-expire.show-more') }}
        </button>
      </div>

      <v-data-footer
        v-if="isExpanded"
        class="expiring-section__pagination"
        :pagination="{
          page: tablePagination.page,
          itemsPerPage: tablePagination.itemsPerPage,
          pageStart: pageStart - 1,
          pageStop: pageStop,
          pageCount: pageCount,
          itemsLength: total,
        }"
        :options="{
          page: tablePagination.page,
          itemsPerPage: tablePagination.itemsPerPage,
          sortBy: [],
          sortDesc: [],
          groupBy: [],
          groupDesc: [],
          mustSort: false,
          multiSort: false,
        }"
        :items-per-page-options="ITEMS_PER_PAGE_OPTIONS"
        :items-per-page-text="$t('offer-manager.offers-list.footer-num-x-page')"
        show-first-last-page
        first-icon="fas fa-angle-double-left"
        last-icon="fas fa-angle-double-right"
        prev-icon="fas fa-angle-left"
        next-icon="fas fa-angle-right"
        @update:options="onFooterOptions">
        <template #page-text="{pageStart, pageStop, itemsLength}">
          {{ pageStart }}-{{ pageStop }} {{ $t('honda.registration.list.of') }} {{ itemsLength }}
        </template>
      </v-data-footer>
    </template>
  </section>
</template>

<script lang="ts">
import {computed, defineComponent, PropType, ref, toRefs, watch} from '@vue/composition-api'

import MChip from '@/components/atom/MChip/index.vue'
import MCopy from '@/components/atom/MCopy.vue'
import {useExpiringOffersList} from '@/composables/expiring-offers-list'
import {useGtm} from '@/plugins/gtm'
import {Offer, OfferDoc, OfferType, Pagination, Status} from '@/types/offers'
import {formatDate, isPastToday} from '@/utils/date-helper'
import {offerStatusChip} from '@/utils/offer-status-chip'

import OfferTableActions from './OfferTableActions.vue'

const DEFAULT_VISIBLE_COUNT = 3
const DEFAULT_PAGE_SIZE = 10
const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50]

export default defineComponent({
  name: 'ExpiringOffersSection',
  components: {MChip, MCopy, OfferTableActions},
  props: {
    dealerId: {
      type: String,
      required: true,
    },
    statuses: {
      type: Array as PropType<Status[]>,
      required: true,
    },
    text: {
      type: String,
      default: '',
    },
  },
  setup(props, {root, emit}) {
    const gtm = useGtm()
    const {statuses, text} = toRefs(props)

    const isCollapsed = ref(false)
    const isExpanded = ref(false)
    const tablePagination = ref<Required<Pagination>>({page: 1, itemsPerPage: DEFAULT_PAGE_SIZE})

    const pagination = computed(() => ({
      from: isExpanded.value ? calculateFrom(tablePagination.value) : 0,
      size: isExpanded.value ? tablePagination.value.itemsPerPage : DEFAULT_VISIBLE_COUNT,
    }))

    const {data, total, isLoading, refetch} = useExpiringOffersList<OfferDoc>({
      dealerId: props.dealerId,
      statuses,
      text,
      pagination,
    })

    const isVisible = computed(() => statuses.value.includes(Status.Accepted) && total.value > 0)

    const pageCount = computed(() =>
      tablePagination.value.itemsPerPage > 0 ? Math.ceil(total.value / tablePagination.value.itemsPerPage) : 1,
    )

    const pageStart = computed(() =>
      total.value === 0 ? 0 : (tablePagination.value.page - 1) * tablePagination.value.itemsPerPage + 1,
    )

    const pageStop = computed(() =>
      Math.min(tablePagination.value.page * tablePagination.value.itemsPerPage, total.value),
    )

    const headers = [
      {text: '', align: 'center', value: 'createDate', width: '10%', sortable: false},
      {text: '', align: 'left', value: 'client', width: '20%', sortable: false},
      {text: '', align: 'left', value: 'vehicle.model', width: '10%', sortable: false},
      {text: '', align: 'center', value: 'offerCode', width: '10%', sortable: false},
      {text: '', align: 'left', value: 'attendedBy', width: '20%', sortable: false},
      {text: '', align: 'left', value: 'status', width: '15%', sortable: false},
      {text: '', align: 'center', value: 'followUp', width: '10%', sortable: false},
      {text: '', align: 'center', value: 'actions', width: '5%', sortable: false},
    ]

    watch(
      [statuses, text],
      () => {
        tablePagination.value.page = 1
      },
      {deep: true},
    )

    function toggleCollapsed() {
      isCollapsed.value = !isCollapsed.value
    }

    function toggleExpanded() {
      isExpanded.value = !isExpanded.value
      tablePagination.value = {page: 1, itemsPerPage: DEFAULT_PAGE_SIZE}
      refetch()
    }

    function calculateFrom({page, itemsPerPage}: Required<Pagination>) {
      return !page ? 0 : (page - 1) * (itemsPerPage || DEFAULT_PAGE_SIZE)
    }

    function onFooterOptions(options: {page: number; itemsPerPage: number}) {
      const nextItemsPerPage = options.itemsPerPage ?? tablePagination.value.itemsPerPage
      const itemsPerPageChanged = nextItemsPerPage !== tablePagination.value.itemsPerPage
      tablePagination.value = {
        page: itemsPerPageChanged ? 1 : (options.page ?? tablePagination.value.page),
        itemsPerPage: nextItemsPerPage,
      }
      refetch()
    }

    function getRowClass() {
      return 'expiring-table__row click'
    }

    function statusChip(offer: any) {
      return offerStatusChip(offer, (key: string, params?: Record<string, unknown>) =>
        root.$t(key, params || {}).toString(),
      )
    }

    function getFullName(offer: OfferDoc) {
      return `${offer.client?.name || ''} ${offer.client?.surname || ''}`.trim()
    }

    function getModel(offer: OfferDoc) {
      return (offer.vehicle?.model?.name || '').split(' ')[0]
    }

    function viewOfferSummary(offer: Offer) {
      if (!offer?.id) return
      gtm?.trackEvent({
        category: 'Offers',
        action: 'ViewOfferSummary',
        label: 'FromExpiringSection',
      })
      root.$router.push({name: 'ResumeOfferMotorbike', params: {id: offer.id}})
    }

    function onChangeStatus(offer: Offer, status: Status) {
      emit('changeStatus', offer, status)
    }

    function onCopyClick() {
      root.$store.dispatch('showAlertSuccess', 'common.copy-success')
    }

    function handleHistoryClick(id: string) {
      if (!id) return
      root.$router.push({name: 'ClientHistory', params: {id}})
    }

    return {
      DEFAULT_VISIBLE_COUNT,
      ITEMS_PER_PAGE_OPTIONS,
      OfferType,
      data,
      total,
      isLoading,
      isCollapsed,
      isExpanded,
      isVisible,
      tablePagination,
      pageCount,
      pageStart,
      pageStop,
      headers,
      formatDate,
      isPastToday,
      statusChip,
      getFullName,
      getModel,
      getRowClass,
      toggleCollapsed,
      toggleExpanded,
      onFooterOptions,
      viewOfferSummary,
      onChangeStatus,
      onCopyClick,
      handleHistoryClick,
      refetch,
    }
  },
})
</script>

<style scoped>
.expiring-section {
  margin-bottom: 24px;
}

.expiring-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  cursor: pointer;
  user-select: none;
}

.expiring-section__header--collapsed {
  border-bottom: 1px solid #e0e0e0;
}

.expiring-section__title-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.expiring-section__title {
  font:
    normal normal 600 24px/30px 'ProximaNovaExCnSBold',
    sans-serif;
  text-transform: uppercase;
  color: var(--v-primary-base, #dc182d);
  margin: 0;
  letter-spacing: 0;
}

.expiring-section__info-icon {
  color: var(--v-tertiary-base, #0075eb);
  font-size: 18px;
  cursor: pointer;
}

.expiring-section__chevron {
  margin-left: auto;
}

.expiring-section__body {
  background: #fff1d933;
  border: 1px solid #bbb;
  border-radius: 8px;
  padding: 4px 0;
}

.text-expired {
  color: #dc182d;
  font-size: 14px;
}

.expiring-section__toggle {
  display: block;
  width: 100%;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 12px;
  text-align: center;
  font:
    normal normal 600 18px/24px 'ProximaNovaExCnSBold',
    sans-serif;
  color: #9a9a9a;
  text-transform: uppercase;
  letter-spacing: 0;
  border-top: 1px dashed rgba(0, 0, 0, 0.12);
  margin-top: 4px;
}

.expiring-section__toggle:hover {
  color: var(--v-primary-base, #dc182d);
}

.expiring-section__pagination {
  background: transparent;
  border-top: none;
}
</style>

<style>
.expiring-section__body .expiring-table.v-data-table,
.expiring-section__body .expiring-table .v-data-table__wrapper,
.expiring-section__body .expiring-table table,
.expiring-section__body .expiring-table tbody,
.expiring-section__body .expiring-table tbody > tr,
.expiring-section__body .expiring-table tbody > tr > td {
  background-color: transparent !important;
}

.expiring-section__body .expiring-table tbody > tr {
  cursor: pointer;
}

.expiring-section__body .expiring-table tbody > tr > td {
  border-bottom: 1px solid #bbb !important;
}

.expiring-section__body .expiring-table tbody > tr:last-of-type > td {
  border-bottom: none !important;
}

.expiring-section__body .expiring-table tbody > tr:hover {
  background-color: rgba(0, 0, 0, 0.03) !important;
}

.expiring-section__body .expiring-table thead > tr {
  height: 0 !important;
}

.expiring-section__body .expiring-table thead > tr > th {
  height: 0 !important;
  padding: 0 !important;
  border: none !important;
}

.expiring-section__body .expiring-table tbody > tr > td .text-item,
.expiring-section__body .expiring-table tbody > tr > td .text-expired,
.expiring-section__body .expiring-table tbody > tr > td .customer-header {
  font-family: 'ProximaNovaSemibold', sans-serif !important;
}

.expiring-section__body .expiring-table tbody > tr > td .customer-header {
  margin: 0 !important;
  padding: 0 !important;
}

.expiring-section__body .expiring-table tbody > tr > td .customer-email,
.expiring-section__body .expiring-table tbody > tr > td .customer-phone {
  font-family: 'ProximaNovaRegular', sans-serif;
  color: var(--v-gray-lighten5);
  font-size: 14px;
}
</style>
